import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { aboutContent } from '@/mocks/faq';

const baseUrl = 'https://qiqicinema.com';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormName('');
      setFormEmail('');
      setFormMessage('');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9n3e7e8mbnljin59tc0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.get('name') as string,
          email: formData.get('email') as string,
          message: formData.get('message') as string,
        }).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormName('');
        setFormEmail('');
        setFormMessage('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        name: '关于琪琪影院 - 高清影视在线观看平台',
        url: `${baseUrl}/about`,
        isPartOf: { '@type': 'WebSite', name: '琪琪影院', url: baseUrl },
      },
      {
        '@type': 'Organization',
        name: '琪琪影院',
        url: baseUrl,
        description: aboutContent.brandIntro.paragraphs[0],
        contactPoint: {
          '@type': 'ContactPoint',
          email: aboutContent.contact.email,
          contactType: 'customer service',
          availableLanguage: ['Chinese'],
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: baseUrl },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于琪琪影院 - 高清影视蓝光画质在线观看平台 | 品牌介绍与联系方式"
        description="琪琪影院成立于2020年，是国内领先的高清影视在线观看平台。了解琪琪影院的品牌故事、口碑评价和联系方式。"
        keywords="琪琪影院,关于琪琪影院,琪琪影院品牌介绍,琪琪影院联系方式,高清影视平台"
        canonicalUrl={`${baseUrl}/about`}
        ogType="website"
        schema={schema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16 md:pt-18">
        {/* Hero */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-screen-xl mx-auto px-4 md:px-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mt-2">关于我们</h1>
            <p className="text-sm text-foreground-500 mt-2">了解琪琪影院的品牌故事与发展历程</p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-screen-xl mx-auto px-4 md:px-8">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
              <div className="flex-1">
                <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading mb-4">
                  {aboutContent.brandIntro.title}
                </h2>
                {aboutContent.brandIntro.paragraphs.map((p, i) => (
                  <p key={i} className="text-sm text-foreground-600 leading-relaxed mb-3">{p}</p>
                ))}
              </div>
              <div className="w-full lg:w-[420px] shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20modern%20minimalist%20office%20interior%20with%20a%20large%20screen%20displaying%20movie%20posters%2C%20warm%20ambient%20lighting%2C%20comfortable%20workspace%20with%20wooden%20desk%2C%20creative%20atmosphere%2C%20cinematic%20theme%20decor%2C%20professional%20yet%20cozy%20setting&width=800&height=600&seq=about-brand-cinema&orientation=landscape"
                  alt="琪琪影院品牌形象"
                  title="琪琪影院品牌形象"
                  className="w-full rounded-lg object-cover"
                />
              </div>
            </div>

            {/* Values */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-10">
              {aboutContent.brandIntro.values.map((v) => (
                <div key={v.title} className="p-4 md:p-5 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">{v.title}</h3>
                  <p className="text-xs text-foreground-500">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-screen-xl mx-auto px-4 md:px-8">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading text-center mb-2">
              {aboutContent.reputation.title}
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">来自用户的真实评价</p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {aboutContent.reputation.stats.map((stat) => (
                <div key={stat.label} className="p-5 rounded-lg bg-background-50 border border-background-200/70 text-center">
                  <div className="text-xl md:text-2xl font-bold text-primary-500 font-heading">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Reviews */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {aboutContent.reputation.reviews.map((review) => (
                <div key={review.name} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">{review.content}</p>
                  <p className="text-xs font-semibold text-foreground-900">{review.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-screen-xl mx-auto px-4 md:px-8">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading text-center mb-2">
              {aboutContent.contact.title}
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">如有任何问题，欢迎联系我们</p>

            <div className="flex flex-col lg:flex-row gap-8">
              {/* Contact Info */}
              <div className="flex-1 space-y-4">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-lg text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950">电子邮箱</h3>
                    <p className="text-sm text-foreground-600">{aboutContent.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                    <i className="ri-wechat-line text-lg text-accent-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950">微信公众号</h3>
                    <p className="text-sm text-foreground-600">{aboutContent.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center shrink-0">
                    <i className="ri-time-line text-lg text-secondary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950">工作时间</h3>
                    <p className="text-sm text-foreground-600">{aboutContent.contact.workingHours}</p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="flex-1">
                <form
                  data-readdy-form
                  id="contact-form"
                  onSubmit={handleSubmit}
                  className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <h3 className="text-base font-semibold text-foreground-950 mb-4 font-heading">发送消息</h3>

                  {formStatus === 'success' && (
                    <div className="mb-4 p-3 rounded bg-primary-50 border border-primary-200 text-sm text-primary-700">
                      <i className="ri-check-line mr-1"></i> 消息发送成功！我们会尽快回复您。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-sm text-red-700">
                      <i className="ri-error-warning-line mr-1"></i> {formError}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="form-name" className="block text-xs font-medium text-foreground-700 mb-1">姓名</label>
                      <input
                        id="form-name"
                        type="text"
                        name="name"
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        required
                        className="w-full px-3 py-2 text-sm rounded border border-background-300 bg-background-50 text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="form-email" className="block text-xs font-medium text-foreground-700 mb-1">邮箱</label>
                      <input
                        id="form-email"
                        type="email"
                        name="email"
                        value={formEmail}
                        onChange={(e) => setFormEmail(e.target.value)}
                        required
                        className="w-full px-3 py-2 text-sm rounded border border-background-300 bg-background-50 text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="form-message" className="block text-xs font-medium text-foreground-700 mb-1">消息</label>
                      <textarea
                        id="form-message"
                        name="message"
                        value={formMessage}
                        onChange={(e) => {
                          if (e.target.value.length <= 500) setFormMessage(e.target.value);
                        }}
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-3 py-2 text-sm rounded border border-background-300 bg-background-50 text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的消息（不超过500字）"
                      ></textarea>
                      <div className="text-xs text-foreground-400 mt-1 text-right">{formMessage.length}/500</div>
                    </div>
                    {/* Honeypot */}
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    <button
                      type="submit"
                      className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-500 text-white font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-sm"
                    >
                      <i className="ri-send-plane-line"></i>
                      发送消息
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}